import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Upload, FileText, Trash2, Download, Image, File } from 'lucide-react';

export default function PayslipUpload() {
  const [uploads, setUploads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('all');

  // Upload form
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchEmployees();
    fetchUploads();
  }, []);

  useEffect(() => {
    fetchUploads();
  }, [filterMonth, filterEmployee]);

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data);
    } catch (error) {
      console.error('Failed to fetch employees');
    }
  };

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterEmployee !== 'all') params.append('employee_id', filterEmployee);
      const res = await api.get(`/payslip-uploads?${params.toString()}`);
      setUploads(res.data);
    } catch (error) {
      toast.error('Failed to fetch uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !selectedMonth || !selectedFile) {
      toast.error('Please select employee, month, and file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('employee_id', selectedEmployee);
      formData.append('month', selectedMonth);
      formData.append('file', selectedFile);

      await api.post('/payslip-uploads', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Payslip uploaded successfully');
      setDialogOpen(false);
      setSelectedFile(null);
      setSelectedEmployee('');
      fetchUploads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (upload) => {
    try {
      const res = await api.get(`/payslip-uploads/${upload.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaySlip-${upload.employee_name.replace(/ /g, '_')}-${upload.month}.${upload.filename.split('.').pop()}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payslip?')) return;
    try {
      await api.delete(`/payslip-uploads/${id}`);
      toast.success('Payslip deleted');
      fetchUploads();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType) => {
    if (contentType === 'application/pdf') return <FileText className="w-5 h-5 text-red-500" />;
    return <Image className="w-5 h-5 text-blue-500" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payslip-upload-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800">Upload Payslips</h1>
          <p className="text-slate-500">Upload PDF or Image payslips for employees</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#FF9933] hover:bg-[#E68A2E] gap-2" data-testid="upload-payslip-btn">
          <Upload className="w-4 h-4" />
          Upload Payslip
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm shrink-0">Month</Label>
              <Input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="w-44"
                data-testid="filter-month"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm shrink-0">Employee</Label>
              <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                <SelectTrigger className="w-[200px]" data-testid="filter-employee">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(filterMonth || filterEmployee !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterMonth(''); setFilterEmployee('all'); }}>
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Uploads Table */}
      <Card className="bg-white border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <File className="w-5 h-5 text-slate-500" />
            Uploaded Payslips ({uploads.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="uploads-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Employee</TableHead>
                  <TableHead className="font-semibold">Code</TableHead>
                  <TableHead className="font-semibold">Month</TableHead>
                  <TableHead className="font-semibold">File</TableHead>
                  <TableHead className="font-semibold">Size</TableHead>
                  <TableHead className="font-semibold">Uploaded</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
                ) : uploads.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No payslips uploaded yet</TableCell></TableRow>
                ) : (
                  uploads.map((u) => (
                    <TableRow key={u.id} data-testid={`upload-row-${u.id}`}>
                      <TableCell className="font-medium text-slate-800">{u.employee_name}</TableCell>
                      <TableCell className="font-mono text-[#FF9933]">{u.employee_code}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{u.month}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getFileIcon(u.content_type)}
                          <span className="text-sm text-slate-600 truncate max-w-[200px]">{u.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{formatFileSize(u.file_size)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(u.uploaded_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(u)} data-testid={`download-${u.id}`}>
                            <Download className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} data-testid={`delete-${u.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Payslip</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="upload-employee-select">
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Month *</Label>
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                required
                data-testid="upload-month-input"
              />
            </div>

            <div className="space-y-2">
              <Label>File (PDF or Image) *</Label>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                required
                data-testid="upload-file-input"
              />
              {selectedFile && (
                <p className="text-xs text-slate-500">
                  {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={uploading} className="bg-[#FF9933] hover:bg-[#E68A2E]" data-testid="submit-upload-btn">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
